// eslint-disable-next-line max-classes-per-file
import { OrganizationUser } from '@enview/interface/types/OrganizationUser';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import {
  BillAction,
  BillDocumentSelection,
  Hearing,
} from '@enview/interface/types/bills/Bill';
import { BillVote } from '@enview/interface/types/bills/BillVote';
import PersonVote from '@enview/interface/types/votes/PersonVote';
import moment from 'moment';
import React from 'react';
import PersonVoteCard from '../../Person/PersonVoteCard';
import BillActivityCard from './ActivityCards/BillActivityCard';
import HearingActivityCard from './ActivityCards/HearingActivityCard';
import UserActivityCard from './ActivityCards/UserActivityCard';
import VoteActivityCard from './ActivityCards/VoteActivityCard';
import { PositionStatement } from '@enview/interface/types/bills/PositionStatement';
import {
  convertCustomFieldToUserAction,
  convertPositionStatementToUserAction,
} from './ActivityFeedHelper';
import PositionActivityCard from '../BillPosition/PositionActivityCard';
import { BillCustomField } from '@enview/interface/types/CustomFields';

export enum ActivityType {
  BILL_ACTION = 'bill-action',
  BILL_HEARING = 'bill-hearing',
  USER_ACTION = 'user-action',
  BILL_VOTE = 'bill-vote',
  PERSON_VOTE = 'person-vote',
}

export type Activity = {
  date: moment.Moment;
  type: ActivityType;
  isArchived: boolean;
  isStarred?: boolean;
  teamIds?: number[];
  order: number;
  render(): React.ReactNode;
};

export class BillActivity implements Activity {
  date: moment.Moment;

  isArchived: boolean;

  render: () => React.ReactNode;

  type: ActivityType;

  order: number;

  constructor(billAction: BillAction, render: () => React.ReactNode) {
    this.date = moment.utc(billAction.actionDate || billAction.createdAt);
    this.type = ActivityType.BILL_ACTION;
    this.isArchived = false;
    this.order = billAction.order;
    this.render = render;
  }

  static transformBillActions = (actions: BillAction[]): Activity[] => {
    return actions.map((action) => {
      return new BillActivity(action, () => (
        <BillActivityCard billAction={action} key={action.id} />
      ));
    });
  };
}

export class HearingActivity implements Activity {
  date: moment.Moment;

  isArchived: boolean;

  render: () => React.ReactNode;

  type: ActivityType;

  order: number;

  constructor(hearing: Hearing, render: () => React.ReactNode) {
    this.date = moment.utc(hearing.start || hearing.createdAt);
    this.type = ActivityType.BILL_HEARING;
    this.isArchived = false;
    this.order = -1;
    this.render = render;
  }

  static transformHearings = (hearings: Hearing[]): Activity[] => {
    return hearings.map((hearing) => {
      return new HearingActivity(hearing, () => (
        <HearingActivityCard hearing={hearing} />
      ));
    });
  };
}

export class VoteActivity implements Activity {
  date: moment.Moment;

  isArchived: boolean;

  render: () => React.ReactNode;

  type: ActivityType;

  order: number;

  constructor(billVote: BillVote, render: () => React.ReactNode) {
    this.date = moment.utc(billVote.startDate);
    this.type = ActivityType.BILL_VOTE;
    this.isArchived = false;
    this.order = -1;
    this.render = render;
  }

  static transformVotes = (votes: BillVote[]): Activity[] => {
    return votes.map((vote) => {
      return new VoteActivity(vote, () => (
        <VoteActivityCard billVote={vote} key={vote.id} />
      ));
    });
  };
}

export class UserActivity implements Activity {
  date: moment.Moment;

  type: ActivityType;

  isArchived: boolean;

  teamIds: number[];

  isStarred: boolean;

  render: () => React.ReactNode;

  userAction: UserAction;

  order: number;

  constructor(userAction: UserAction, render: () => React.ReactNode) {
    this.date = moment(userAction.updatedAt);
    this.type = ActivityType.USER_ACTION;
    this.isArchived = userAction.isArchived;
    this.isStarred = userAction.isStarred;
    this.teamIds = userAction.teams;
    this.render = render;
    this.userAction = userAction;
    this.order = -1;
  }

  static transformUserActions = (
    actions: UserAction[],
    orgUser: OrganizationUser,
    setSelectedBillVersion?: (version: BillDocumentSelection) => void,
    selectedBillVersion?: BillDocumentSelection,
  ): Activity[] => {
    return actions
      .filter((action) => {
        // TODO remove this when we stop returning tags as useractions
        return (
          action.commentJson ||
          action.comment ||
          action.userFiles.length > 0 ||
          action.annotation
        );
      })
      .map((action) => {
        return new UserActivity(action, () => {
          return (
            <UserActivityCard
              action={action}
              key={action.id}
              orgUser={orgUser}
              selectedBillVersion={selectedBillVersion}
              setSelectedBillVersion={setSelectedBillVersion}
            />
          );
        });
      });
  };

  static transformPositionStatement = (position: PositionStatement): Activity => {
    const action: UserAction = convertPositionStatementToUserAction(position);

    return new UserActivity(action, () => {
      return (
        <PositionActivityCard
          action={action}
          key={'position-' + action.id}
          position={position}
        />
      );
    });
  };

  static transformCustomFieldValue(
    fieldValues: Record<string, Partial<BillCustomField>>,
    orgUser: OrganizationUser,
  ): Activity[] {
    const actions: Array<UserAction & { title: string }> = Object.entries(fieldValues)
      .map(([title, fieldValue]) => {
        const baseAction = convertCustomFieldToUserAction(fieldValue);
        if (!baseAction) return null;
        return {
          ...baseAction,
          title,
        };
      })
      .filter((action): action is UserAction & { title: string } => Boolean(action));

    return actions.map(
      ({ title, ...action }) =>
        new UserActivity(action, () => (
          <UserActivityCard
            action={action}
            hideFooter
            isEditable={false}
            key={action.id}
            orgUser={orgUser}
            subject={title}
          />
        )),
    );
  }
}

export class PersonVoteActivity implements Activity {
  date: moment.Moment;

  isArchived: boolean;

  render: () => React.ReactNode;

  type: ActivityType;

  order: number;

  constructor(personVote: PersonVote, render: () => React.ReactNode) {
    this.date = moment.utc(personVote.billVote.startDate);
    this.type = ActivityType.PERSON_VOTE;
    this.isArchived = false;
    this.order = -1;
    this.render = render;
  }

  static transformPersonVotes = (votes: PersonVote[]): Activity[] => {
    return votes.map((vote) => {
      return new PersonVoteActivity(vote, () => (
        <PersonVoteCard key={vote.voteId} personVote={vote} />
      ));
    });
  };
}
